/**
 * Os 35 toggles da tela Lembretes em 9 grupos (6.10), com os textos do quadro Lembretes.
 * `time` diz de onde vem o horário exibido: um horário fixo editável (time_override), um horário
 * da rotina (refeição, acordar…) ou um rótulo fixo ("Auto", "Sábado"…).
 */
export type ReminderTime =
  | { kind: 'slot'; slot: 'breakfast' | 'lunch' | 'snack' | 'dinner' | 'supper' }
  | { kind: 'fixed'; at: string }
  | { kind: 'label'; text: string };

export type ReminderItem = { key: string; title: string; detail: string; time: ReminderTime };
export type ReminderGroup = { key: string; label: string; kanji: string; color: string; items: ReminderItem[] };

const L = (text: string): ReminderTime => ({ kind: 'label', text });
const F = (at: string): ReminderTime => ({ kind: 'fixed', at });

export const REMINDER_GROUPS: ReminderGroup[] = [
  {
    key: 'meals', label: 'Refeições', kanji: '食', color: 'var(--shu)',
    items: [
      { key: 'meal.breakfast', title: 'Café da manhã', detail: 'As 3 opções de café', time: { kind: 'slot', slot: 'breakfast' } },
      { key: 'meal.lunch', title: 'Almoço', detail: 'A marmita do dia + 2 ovos cozidos', time: { kind: 'slot', slot: 'lunch' } },
      { key: 'meal.snack', title: 'Lanche + creatina', detail: 'Shake, 5 g de creatina, 2 pães. Sábado: +1 pão francês', time: { kind: 'slot', slot: 'snack' } },
      { key: 'meal.dinner', title: 'Jantar', detail: 'A marmita do dia', time: { kind: 'slot', slot: 'dinner' } },
      { key: 'meal.supper', title: 'Ceia', detail: 'Shake: 30 g de whey + 250 ml de leite', time: { kind: 'slot', slot: 'supper' } },
      { key: 'meal.missing', title: 'Refeição não registrada', detail: 'Se passar 1 h do horário sem registrar', time: L('+1 h') },
      { key: 'day.close', title: 'Fechamento do dia', detail: 'Se estiver abaixo de 1.950 kcal ou de 160 g de proteína', time: F('21:00') },
      { key: 'free_meal', title: 'Refeição livre liberada', detail: 'À noite, depois do fut7', time: L('Sábado') },
    ],
  },
  {
    key: 'food_routine', label: 'Rotina da comida', kanji: '冷', color: 'var(--shu)',
    items: [
      { key: 'freezer.move', title: 'Freezer → geladeira', detail: 'As 2 marmitas de amanhã, pelo nome', time: F('21:00') },
      { key: 'eggs.cook', title: 'Cozinhar 10 ovos', detail: 'Quando os ovos cozidos da geladeira acabarem', time: L('Auto') },
    ],
  },
  {
    key: 'water', label: 'Água', kanji: '水', color: 'var(--water)',
    items: [
      { key: 'water.behind', title: 'Atrás do ritmo', detail: 'Das 9h às 21h, só quando estiver atrás do plano do dia', time: L('2 em 2 h') },
      { key: 'water.training', title: 'Água pro treino', detail: 'Leve 750 ml', time: L('1 h antes') },
      { key: 'water.game_day', title: 'Meta de 4 L', detail: '500 ml 1 h antes do jogo e goles a cada 15 min', time: L('Sábado') },
      { key: 'water.last', title: 'Última água do dia', detail: '1 h antes de dormir, se ainda faltar', time: L('Noite') },
    ],
  },
  {
    key: 'training', label: 'Treino', kanji: '練', color: 'var(--indigo)',
    items: [
      { key: 'workout.reminder', title: 'Treino do dia', detail: 'Os exercícios e a carga da última vez', time: L('1 h antes') },
      { key: 'game.activation', title: 'Ativação antes do fut7', detail: '4 h antes do jogo', time: L('Sábado') },
      { key: 'phase.jumps', title: 'Saltos liberados', detail: 'Treinos A e C', time: L('Sem. 5') },
      { key: 'phase.hamstring', title: 'Flexora com uma perna', detail: 'Treino A: 3 × 8, uma perna por vez', time: L('Sem. 9') },
      { key: 'deload.upcoming', title: 'Semana leve chegando', detail: 'Antes das semanas 4, 8, 12, 16…', time: L('Domingo') },
      { key: 'fatigue', title: 'Cansaço acumulado', detail: 'Sono abaixo de 7 h ou disposição 1–2 por 3 dias seguidos', time: L('Auto') },
    ],
  },
  {
    key: 'run', label: 'Corrida', kanji: '走', color: 'var(--indigo)',
    items: [
      { key: 'run.blocks', title: 'Corrida nova', detail: 'Quando a corrida de quinta muda de fase', time: L('Sem. 7, 13, 19') },
      { key: 'run.test', title: 'Teste de 2 km', detail: 'Na quinta, na esteira', time: L('Sem. 1, 8, 16, 24') },
    ],
  },
  {
    key: 'study', label: 'Estudo', kanji: '学', color: 'var(--study)',
    items: [
      { key: 'study.daily', title: 'Estudo do dia', detail: 'Se faltar algum idioma, com os minutos que faltam de cada', time: F('20:30') },
      { key: 'study.streak_risk', title: 'Sequência em risco', detail: 'Se um idioma ainda não fechou os 15 min', time: F('22:30') },
      { key: 'study.milestone', title: 'Marcos de sequência', detail: '7, 30, 100 e 365 dias seguidos', time: L('Auto') },
      { key: 'study.weekly_summary', title: 'Resumo da semana', detail: 'Minutos por idioma e cartões revisados', time: L('Domingo') },
    ],
  },
  {
    key: 'stock', label: 'Estoque', kanji: '庫', color: 'var(--shu)',
    items: [
      { key: 'stock.marmitas', title: 'Marmitas acabando', detail: '4 dias antes, com o pedido pronto pra repetir', time: L('Auto') },
      { key: 'stock.market', title: 'Mercado acabando', detail: '3 dias antes, com a lista de compras', time: L('Auto') },
    ],
  },
  {
    key: 'progress', label: 'Progresso', kanji: '進', color: 'var(--shu)',
    items: [
      { key: 'weigh.reminder', title: 'Pesar', detail: 'Seg, qua e sex. Em jejum, depois do banheiro', time: { kind: 'label', text: '07:00' } },
      { key: 'checkin.due', title: 'Check-in', detail: 'Peso, cintura e 3 fotos, às 07:00', time: L('30 dias') },
      { key: 'adjust', title: 'Ajuste de calorias', detail: 'Peso parado 3 semanas ou caindo mais de 0,8 kg por semana', time: L('Auto') },
      { key: 'month.closed', title: 'Meta do mês', detail: 'Compara com o peso alvo de cada mês', time: L('Fim do mês') },
      { key: 'milestone.66kg', title: 'Barriga começa a sair', detail: 'Quando a média chegar a 66 kg', time: L('66 kg') },
    ],
  },
  {
    key: 'health', label: 'Saúde', kanji: '体', color: 'var(--shu)',
    items: [
      { key: 'sleep.prompt', title: 'Sono e disposição', detail: 'Como dormiu e como acordou, em 2 toques', time: L('Ao acordar') },
      { key: 'health.blood_tests', title: 'Exames de sangue', detail: 'Item que falta do começo do plano', time: L('Até fazer') },
    ],
  },
];

export const ALL_SETTING_KEYS: string[] = REMINDER_GROUPS.flatMap((g) => g.items.map((i) => i.key));

/** Regra(s) ligada(s) a cada toggle. `meal.{slot}` mapeia para `meal.reminder` daquela refeição. */
export function rulesForSettingKey(key: string): string[] {
  if (key.startsWith('meal.') && ['breakfast', 'lunch', 'snack', 'dinner', 'supper'].includes(key.slice(5))) {
    return ['meal.reminder'];
  }
  const map: Record<string, string[]> = {
    'day.close': ['day.kcal_low', 'day.protein_low'],
    free_meal: ['free_meal.unlocked'],
    'phase.jumps': ['phase.jumps_unlocked'],
    'phase.hamstring': ['phase.hamstring_unilateral'],
    fatigue: ['fatigue.early_deload'],
    'run.blocks': ['run.continuous_start', 'run.new_block'],
    'run.test': ['run.test_day'],
    'stock.marmitas': ['stock.marmitas_low'],
    'stock.market': ['stock.market_low', 'stock.ends_tomorrow'],
    adjust: ['adjust.stalled', 'adjust.too_fast'],
  };
  return map[key] ?? [key];
}
